import { Input, Button, Select, DatePicker, Form, Spin, message } from 'antd';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import {
  validateEmail,
  validateTkLength,
  validatePhoneNumber,
  validateWebSite,
} from 'utils';
import { Constants } from 'utils/constants';
import { Errors } from 'utils/errors';
import { CompanySizeOptions, LegalFormOptions } from 'utils/enums';
import { types } from 'store/user/types';
import { logoutUser } from 'store/user/actions';
import { ReactComponent as Info } from '../../../assets/icons/info-icon.svg';
import { Tooltip } from 'components';
import BusinessSizeField from 'components/businessSizeField';
import {
  mapApiDataToForm,
  mapFormDataToApi,
} from './ConfirmRegistration.mapper';
import { useConfirmRegistrationQuery } from './useConfirmRegistrationQuery.hook';
import { useConfirmRegistrationMutation } from './useConfirmRegistrationMutation.hook';
import styles from './ConfirmRegistration.module.scss';

/**
 * @summary ConfirmRegistration Page.
 * @description
 * The ConfirmRegistration page allows a user after successful authentication
 * to verify pre-existing information from the "Expression of Interest" step
 * and provide any additional required data such as contact details and business codes (KAD).
 *
 * The page is structured into three main sections:
 * - Company Details
 * - Company Headquarters
 * - Contact Details
 *
 * @returns {JSX.Element} The ConfirmRegistration page component.
 */
export function ConfirmRegistration() {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const isLoggingOut = useSelector(state => state.user.isLoggingOut);
  const { data, isFetching } = useConfirmRegistrationQuery();
  const { mutate, isPending, isSuccess } = useConfirmRegistrationMutation();

  const handleCompanySizeChange = modalData => {
    form.setFieldsValue({
      companySize: modalData.event,
    });
  };

  const handleBackToLogin = () => {
    dispatch(logoutUser());
  };

  const handleConfirmRegistration = async formValues => {
    try {
      await form.validateFields();
      const payload = mapFormDataToApi(formValues);
      mutate(payload);
    } catch (error) {
      message.error(Errors.GenericFormError);
    }
  };

  if (isFetching === true) {
    return (
      <div className={styles.spinner}>
        <Spin size="large" />
      </div>
    );
  }

  /**
   * *** Update Redux on success and let AuthGuard handle the redirection ***
   */
  if (isSuccess === true) {
    dispatch({
      type: types.CHECK_VALIDATED.SUCCESS,
      payload: true,
    });
  }

  const isFormDisabled = isPending || isLoggingOut;
  const initialValues = mapApiDataToForm(data);
  return (
    <div className={styles.wrapper}>
      <section className={styles.container}>
        <Form
          form={form}
          onFinish={handleConfirmRegistration}
          initialValues={initialValues}
          disabled={isFormDisabled}
          layout="vertical"
          className={styles.content}
        >
          <section className={styles.header}>
            <h2 id={styles['title']}>{Constants.ConfirmRegistrationTitle}</h2>
          </section>
          <section className={styles.main}>
            {/*** Company Details::Begin ***/}
            <section id={styles['company-details']}>
              <h3 className={styles.sectionTitle}>
                {Constants.CompanyDetails}
              </h3>
              <section className={styles.sectionRow}>
                <section className={styles.sectionDetails}>
                  <label className={styles.label}>
                    {Constants.CompanyName}{' '}
                    <span className={styles.asterisk}>*</span>
                  </label>
                  <Form.Item
                    name="companyName"
                    className={styles.fullWidth}
                    rules={[
                      {
                        required: true,
                        message: Errors.FieldRequired,
                      },
                    ]}
                    disabled
                  >
                    <Input
                      className={styles['input-field']}
                      placeholder={Placeholders.CompanyName}
                    />
                  </Form.Item>
                </section>
                <section className={styles.sectionDetails}>
                  <label className={styles.label}>
                    {Constants.EstablishmentDate}{' '}
                    <span className={styles.asterisk}>*</span>
                  </label>
                  <Form.Item
                    name="establishmentDate"
                    className={styles.fullWidth}
                    rules={[
                      {
                        required: true,
                        message: Errors.FieldRequired,
                      },
                    ]}
                    disabled
                  >
                    <DatePicker
                      className={styles['input-field']}
                      format="DD-MM-YYYY"
                      placeholder={Placeholders.SelectDate}
                    />
                  </Form.Item>
                </section>
              </section>
              <section className={styles.sectionRow}>
                <section
                  className={styles.sectionDetails}
                  id={styles['align-self-start']}
                >
                  <label className={styles.label}>
                    {Constants.TaxIdentificationNumber}{' '}
                    <span className={styles.asterisk}>*</span>
                  </label>
                  <Form.Item
                    name="taxIdentificationNumber"
                    className={styles.fullWidth}
                    rules={[
                      {
                        required: true,
                        message: Errors.FieldRequired,
                      },
                    ]}
                  >
                    <Input
                      className={styles['input-field']}
                      placeholder={Placeholders.TAX}
                      disabled
                    />
                  </Form.Item>
                </section>
                <section className={styles.sectionDetails}>
                  <label className={styles.label}>
                    {Constants.CompanySize}{' '}
                    <span className={styles.asterisk}>*</span>
                    <Tooltip
                      placement="right"
                      className="ml-1"
                      title={
                        <div className="tooltip-container">
                          <span className="info-icon-content">
                            {Constants.InfoCompanySize}
                            <br />
                            <br />
                            {Constants.InfoCompanySizeSmall}
                            <br />
                            <br />
                            {Constants.InfoCompanySizeBig}
                            <br />
                          </span>
                        </div>
                      }
                    >
                      <Info className="infoIconSmallIban" />
                    </Tooltip>
                  </label>
                  <Form.Item
                    name="companySize"
                    className={styles.fullWidth}
                    rules={[
                      {
                        required: true,
                        message: Errors.FieldRequired,
                      },
                    ]}
                  >
                    <BusinessSizeField
                      inputAdditionalClass={styles['input-field']}
                      shouldShowLabel={false}
                      name="companySize"
                      value={form.getFieldValue('companySize')}
                      onChange={handleCompanySizeChange}
                    />
                  </Form.Item>
                </section>
              </section>
              <section className={styles.sectionRow}>
                <section className={styles.sectionDetails}>
                  <label className={styles.label}>
                    {Constants.MainActivityCode}{' '}
                    <span className={styles.asterisk}>*</span>
                  </label>
                  <Form.Item
                    name="activityCode"
                    className={styles.fullWidth}
                    rules={[
                      {
                        required: true,
                        message: Errors.FieldRequired,
                      },
                    ]}
                  >
                    <Input.TextArea
                      className={styles['textarea-field']}
                      placeholder={Placeholders.ActivityCode}
                    />
                  </Form.Item>
                </section>
                <section
                  className={styles.sectionDetails}
                  id={styles['align-self-start']}
                >
                  <label className={styles.label}>
                    {Constants.LegalForm}{' '}
                    <span className={styles.asterisk}>*</span>
                  </label>
                  <Form.Item
                    name="legalForm"
                    className={styles.fullWidth}
                    rules={[
                      {
                        required: true,
                        message: Errors.FieldRequired,
                      },
                    ]}
                  >
                    <Select
                      className={styles.customSelect}
                      options={Object.keys(LegalFormOptions).map(key => ({
                        value: LegalFormOptions[key],
                        label: LegalFormOptions[key],
                      }))}
                    />
                  </Form.Item>
                </section>
              </section>
            </section>
            {/*** Company Details::End ***/}

            {/*** Company Headquarters::Begin ***/}
            <section id={styles['company-headquarters']}>
              <h3 className={styles.sectionTitle}>
                {Constants.CompanyHeadquarter}
              </h3>
              <section className={styles.sectionRow}>
                <section className={styles.sectionRowHalf}>
                  <section className={styles.sectionDetailsHalf}>
                    <label className={styles.label}>
                      {Constants.StreetNumberHeadquarters}{' '}
                      <span className={styles.asterisk}>*</span>
                    </label>
                    <Form.Item
                      name="streetNumber"
                      className={styles.fullWidth}
                      rules={[
                        {
                          required: true,
                          message: Errors.FieldRequired,
                        },
                      ]}
                    >
                      <Input
                        className={styles['input-field']}
                        placeholder={Placeholders.StreetNumber}
                      />
                    </Form.Item>
                  </section>
                  <section className={styles.sectionDetailsHalf}>
                    <label className={styles.label}>
                      {Constants.PostalCode}{' '}
                      <span className={styles.asterisk}>*</span>
                    </label>
                    <Form.Item
                      name="postalCode"
                      className={styles.fullWidth}
                      rules={[
                        {
                          required: true,
                          message: Errors.FieldRequired,
                        },
                        {
                          validator(_, value) {
                            return validateTkLength(value);
                          },
                        },
                      ]}
                    >
                      <Input
                        className={styles['input-field']}
                        placeholder={Placeholders.PostalCode}
                      />
                    </Form.Item>
                  </section>
                </section>
                <section className={styles.sectionDetails}>
                  <label className={styles.label}>{Constants.Website}</label>
                  <Form.Item
                    name="website"
                    className={styles.fullWidth}
                    rules={[
                      {
                        validator(_, value) {
                          return validateWebSite(value);
                        },
                      },
                    ]}
                  >
                    <Input
                      className={styles['input-field']}
                      placeholder={Placeholders.Website}
                    />
                  </Form.Item>
                </section>
              </section>
              <section className={styles.sectionRow}>
                <section className={styles.sectionRowHalf}>
                  <section className={styles.sectionDetailsHalf}>
                    <label className={styles.label}>
                      {Constants.TaxOffice}{' '}
                      <span className={styles.asterisk}>*</span>
                    </label>
                    <Form.Item
                      name="taxOffice"
                      className={styles.fullWidth}
                      rules={[
                        {
                          required: true,
                          message: Errors.FieldRequired,
                        },
                      ]}
                    >
                      <Input
                        className={styles['input-field']}
                        placeholder={Placeholders.TaxOffice}
                      />
                    </Form.Item>
                  </section>
                  <section className={styles.sectionDetailsHalf}>
                    <label className={styles.label}>
                      {Constants.Number}{' '}
                      <span className={styles.asterisk}>*</span>
                    </label>
                    <Form.Item
                      name="phoneHeadquarters"
                      className={styles.fullWidth}
                      rules={[
                        {
                          required: true,
                          message: Errors.FieldRequired,
                        },
                        {
                          validator(_, value) {
                            return validatePhoneNumber(value, false);
                          },
                        },
                      ]}
                    >
                      <Input
                        className={styles['input-field']}
                        placeholder={Placeholders.Number}
                      />
                    </Form.Item>
                  </section>
                </section>
                <section className={styles.sectionDetails}>
                  <label className={styles.label}>
                    {Constants.Email} <span className={styles.asterisk}>*</span>
                  </label>
                  <Form.Item
                    name="emailHeadquarters"
                    className={styles.fullWidth}
                    rules={[
                      {
                        required: true,
                        message: Errors.FieldRequired,
                      },
                      {
                        validator(_, value) {
                          return validateEmail(_, value);
                        },
                      },
                    ]}
                  >
                    <Input
                      className={styles['input-field']}
                      placeholder={Placeholders.EmailHeadquarters}
                    />
                  </Form.Item>
                </section>
              </section>
            </section>
            {/*** Company Headquarters::End ***/}

            {/*** Contact Details::Begin ***/}
            <section id={styles['contact-details']}>
              <h3 className={styles.sectionTitle}>
                {Constants.CompanyDetails}
              </h3>
              <section className={styles.sectionRow}>
                <section className={styles.sectionDetails}>
                  <label className={styles.label}>
                    {Constants.FirstName}{' '}
                    <span className={styles.asterisk}>*</span>
                  </label>
                  <Form.Item
                    name="firstName"
                    className={styles.fullWidth}
                    rules={[
                      {
                        required: true,
                        message: Errors.FieldRequired,
                      },
                    ]}
                  >
                    <Input
                      className={styles['input-field']}
                      placeholder={Placeholders.FirstName}
                    />
                  </Form.Item>
                </section>
                <section className={styles.sectionDetails}>
                  <label className={styles.label}>
                    {Constants.LastName}{' '}
                    <span className={styles.asterisk}>*</span>
                  </label>
                  <Form.Item
                    name="lastName"
                    className={styles.fullWidth}
                    rules={[
                      {
                        required: true,
                        message: Errors.FieldRequired,
                      },
                    ]}
                  >
                    <Input
                      className={styles['input-field']}
                      placeholder={Placeholders.LastName}
                    />
                  </Form.Item>
                </section>
              </section>
              <section className={styles.sectionRow}>
                <section className={styles.sectionDetails}>
                  <label className={styles.label}>
                    {Constants.Occupation}{' '}
                    <span className={styles.asterisk}>*</span>
                  </label>
                  <Form.Item
                    name="occupation"
                    className={styles.fullWidth}
                    rules={[
                      {
                        required: true,
                        message: Errors.FieldRequired,
                      },
                    ]}
                  >
                    <Input
                      className={styles['input-field']}
                      placeholder={Placeholders.Occupation}
                    />
                  </Form.Item>
                </section>
                <section className={styles.sectionDetails}>
                  <label className={styles.label}>
                    {Constants.PhoneNumber}{' '}
                    <span className={styles.asterisk}>*</span>
                  </label>
                  <Form.Item
                    name="phoneContact"
                    className={styles.fullWidth}
                    rules={[
                      {
                        required: true,
                        message: Errors.FieldRequired,
                      },
                      {
                        validator(_, value) {
                          return validatePhoneNumber(value, true);
                        },
                      },
                    ]}
                  >
                    <Input
                      className={styles['input-field']}
                      placeholder={Placeholders.PhoneNumber}
                    />
                  </Form.Item>
                </section>
              </section>
              <section className={styles.sectionRow}>
                <section className={styles.sectionDetails}>
                  <label className={styles.label}>
                    {Constants.Email} <span className={styles.asterisk}>*</span>
                  </label>
                  <Form.Item
                    name="emailContact"
                    className={styles.fullWidth}
                    rules={[
                      {
                        required: true,
                        message: Errors.FieldRequired,
                      },
                      {
                        validator(_, value) {
                          return validateEmail(_, value);
                        },
                      },
                    ]}
                  >
                    <Input
                      className={styles['input-field']}
                      placeholder={Placeholders.EmailContact}
                    />
                  </Form.Item>
                </section>
                <section className={styles.sectionDetails} />
              </section>
            </section>
            {/*** Contact Details::End ***/}
          </section>
          <section className={styles.footer}>
            <Button
              className={styles.button}
              onClick={handleBackToLogin}
              id={styles['logout-button']}
            >
              {isLoggingOut ? <Spin size="default" /> : Constants.BackToLogin}
            </Button>
            <Button
              className={styles.button}
              id={styles['submit-button']}
              htmlType="submit"
            >
              {isPending ? <Spin size="default" /> : Constants.Submit}
            </Button>
          </section>
        </Form>
      </section>
    </div>
  );
}

const Placeholders = {
  Day: dayjs('2022-01-12', 'YYYY-MM-DD'),
  CompanySize: CompanySizeOptions.Medium,
  ActivityCode:
    '72.19.21 - Υπηρεσίες έρευνας και πειραματικής ανάπτυξης στη νανοτεχνολογία',
  LegalForm: 'ΑΝΩΝΥΜΗ ΕΤΑΙΡΙΑ',
  SelectDate: 'Επέλεξε Ημερομηνία',
  CompanyName: 'ΑΝΤΩΝΑΤΟΥ Α.Ε.',
  TAX: '180993245',
  StreetNumber: 'ΠΑΛΑΙΟΛΟΓΟΥ 1',
  PostalCode: '17124',
  Website: 'www.antonatou.gr',
  TaxOffice: 'Α AΘΗΝΩΝ',
  Number: '2107243444',
  EmailHeadquarters: 'info@antonatou.com',
  FirstName: 'Ιωάννα',
  LastName: 'Αντωνάτου',
  Occupation: 'Λογίστρια',
  PhoneNumber: '6945454545',
  EmailContact: 'ioanna.antonatou@gmail.com',
};
